import { UserResponse } from '@/application/requests/auth';
import { ProfileData } from '@/application/requests/auth';
import { STORAGE_KEYS } from './storageKeys';

export { STORAGE_KEYS } from './storageKeys';

/**
 * Checks if user is logged in by verifying AUTH_TOKEN exists.
 * @returns Token string if logged in, false otherwise
 */
export function loggedIn() {
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    ? localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    : false;
  return token;
}

/**
 * Clears all auth tokens and redirects to login page.
 */
export function isForceClearLogout() {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
  localStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
  window.location.href = '/login';
}

/**
 * Stores session token in localStorage.
 * @param token - Session token string
 * @returns localStorage result or null
 */
export const setSessionToken = (token: string) => {
  if (token) {
    return localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token);
  }
  return null;
};

/**
 * Retrieves session token from localStorage.
 * @returns Session token or null
 */
export const getSessionToken = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
};

/**
 * Removes session token from localStorage.
 */
export function isForceClearSessionToken() {
  localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
}

/**
 * Stores password change token in localStorage.
 * @param token - Password change token string
 * @returns localStorage result or null
 */
export const setPasswordChangeToken = (token: string) => {
  if (token) {
    return localStorage.setItem(STORAGE_KEYS.PASSWORD_CHANGE_TOKEN, token);
  }
  return null;
};

/**
 * Retrieves password change token from localStorage.
 * @returns Password change token or null
 */
export const getPasswordChangeToken = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEYS.PASSWORD_CHANGE_TOKEN);
};

/**
 * Removes password change token from localStorage.
 */
export function isForceClearPasswordChangeToken() {
  localStorage.removeItem(STORAGE_KEYS.PASSWORD_CHANGE_TOKEN);
}

/**
 * Stores auth session indicator in localStorage (NOT the token itself — token is in HttpOnly cookie).
 * Decodes the token only to sync user profile with latest permissions.
 * @param token - Auth token string (used for decoding only, never persisted)
 */
export const setAuthToken = (token: string) => {
  if (token) {
    // Store a non-sensitive indicator that a session exists (not the actual token)
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, '1');
    // Decode token to update USER_PROFILE with latest permissions
    syncProfileFromToken(token);
  }
  return null;
};

/**
 * Stores user profile in localStorage.
 * @param user - User profile object
 * @returns localStorage result or null
 */
export const setUserProfile = (user: UserResponse) => {
  if (user) {
    return localStorage.setItem(
      STORAGE_KEYS.USER_PROFILE,
      JSON.stringify(user)
    );
  }
  return null;
};

/**
 * Updates user profile in localStorage with partial data.
 * @param user - Partial profile fields to merge
 * @returns localStorage result or null
 */
export const updateUserProfile = (user: Partial<ProfileData>) => {
  if (user) {
    const userProfile = getUserProfile();
    if (userProfile) {
      return localStorage.setItem(
        STORAGE_KEYS.USER_PROFILE,
        JSON.stringify({
          ...userProfile,
          first_name: user.first_name,
          last_name: user.last_name,
          phone: user.phone,
        })
      );
    }
  }
  return null;
};

/**
 * Retrieves user profile from localStorage.
 * @returns Parsed user profile object or empty object
 */
export const getUserProfile = () => {
  if (typeof window === 'undefined') return null;
  return JSON.parse(localStorage.getItem(STORAGE_KEYS.USER_PROFILE) || '{}');
};

/**
 * Returns user profile with role conversion for compatibility (user_type -> role).
 * Use this when checking isBusiness/isStaff or displaying user role.
 */
export const getProfileWithRole = () => {
  const profile = getUserProfile();
  if (!profile || Object.keys(profile).length === 0) return null;
  const result = { ...profile };
  if (result.user_type && !result.role) {
    result.role = result.user_type === 'business' ? 'Business' : 'Staff';
  }
  return result;
};

/** Dashboard and profile have no permission key in USER_PROFILE but must always be accessible */
const ALWAYS_ALLOWED_PERMISSIONS = ['dashboard', 'profile', 'business_storefront_template'];

/** Temporary permission aliases for routes/pages whose dedicated backend permissions are not available yet. */
const PERMISSION_ALIASES: Record<string, string[]> = {
  // staff_qr_monitor: ['staff_transactions'],
  // business_qr_monitor: ['business_reports'],
};

const getPermissionCandidates = (permissionKey: string) => {
  const [baseKey, ...rest] = permissionKey.split('.');
  const actionSuffix = rest.length > 0 ? `.${rest.join('.')}` : '';
  const aliases = PERMISSION_ALIASES[baseKey] ?? [];

  return new Set([
    permissionKey,
    baseKey,
    ...aliases,
    ...aliases.map((alias) => `${alias}${actionSuffix}`),
  ]);
};

/**
 * Returns permissions in Redux format [{key, is_active}] from USER_PROFILE.
 * When permissions is null/undefined, returns ALWAYS_ALLOWED (dashboard, profile only).
 * Returns null if no USER_PROFILE (e.g. not logged in).
 */
export const getPermissionsFromStorageForRedux = (): Array<{
  key: string;
  is_active: boolean;
}> | null => {
  if (typeof window === 'undefined') return null;
  const user = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
  if (!user) return null;
  try {
    const parsed = JSON.parse(user);
    const permissions = parsed?.permissions;
    const hasNoPermissions =
      permissions === undefined ||
      permissions === null ||
      (Array.isArray(permissions) && permissions.length === 0) ||
      !Array.isArray(permissions);
    if (hasNoPermissions) {
      return ALWAYS_ALLOWED_PERMISSIONS.map((key) => ({
        key,
        is_active: true,
      }));
    }
    return permissions.map(
      (p: string | { key?: string; is_active?: boolean }) => {
        if (typeof p === 'string') return { key: p, is_active: true };
        return { key: p?.key ?? '', is_active: p?.is_active ?? true };
      }
    );
  } catch {
    return null;
  }
};

export const getUserPermission = () => {
  if (typeof window === 'undefined') return null;
  const user = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
  if (!user) return null;
  const parsed = JSON.parse(user);
  const permissions = parsed?.permissions;
  const hasNoPermissions =
    permissions === undefined ||
    permissions === null ||
    (Array.isArray(permissions) && permissions.length === 0) ||
    !Array.isArray(permissions);
  if (hasNoPermissions) {
    return ALWAYS_ALLOWED_PERMISSIONS;
  }
  return permissions;
};

/**
 * Check if user has a specific permission (e.g. 'staff_business_registrations.read')
 * Supports permissions as array of strings or array of objects with key property
 * Dashboard and profile are always allowed (no permission key required)
 */
export const hasUserPermission = (permissionKey: string): boolean => {
  if (ALWAYS_ALLOWED_PERMISSIONS.includes(permissionKey)) return true;
  // Dashboard and profile are always allowed (e.g. 'dashboard.read', 'profile.read')
  const baseKey = permissionKey.split('.')[0];
  if (baseKey && ALWAYS_ALLOWED_PERMISSIONS.includes(baseKey)) return true;

  const permissions = getUserPermission();
  if (!permissions || !Array.isArray(permissions)) return false;

  const permissionCandidates = getPermissionCandidates(permissionKey);

  return permissions.some((p: string | { key?: string }) => {
    const key = typeof p === 'string' ? p : p?.key;
    if (!key) return false;
    if (permissionCandidates.has(key)) return true;
    // Backend may store "staff_users" while we check "staff_users.read"
    if (!key.includes('.')) {
      return Array.from(permissionCandidates).some((candidate) =>
        candidate.startsWith(key + '.')
      );
    }
    return false;
  });
};

export function getAuthToken() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

/**
 * Decodes a JWT access token and updates USER_PROFILE in localStorage
 * so that cached permissions stay in sync with the backend.
 */
export function syncProfileFromToken(accessToken: string) {
  try {
    const base64Url = accessToken.split('.')[1];
    if (!base64Url) return;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const decoded = JSON.parse(jsonPayload);
    if (decoded) {
      setUserProfile(decoded);
    }
  } catch {
    // Silently ignore decode errors
  }
}

/**
 * Stores language preference in localStorage.
 * @param lng - Language code (e.g. 'en', 'th')
 * @returns localStorage result or null
 */
export function setLang(lng: string) {
  if (typeof window !== 'undefined' && lng) {
    return localStorage.setItem(STORAGE_KEYS.I18N_LANG, lng);
  }
  return null;
}

/**
 * Retrieves language preference from localStorage.
 * @returns Language code or null
 */
export function getLang() {
  if (typeof window !== 'undefined') {
    const lng = localStorage.getItem(STORAGE_KEYS.I18N_LANG);
    if (lng) return lng;
    return null;
  }
  return null;
}

/**
 * Stores refresh session indicator in localStorage (NOT the token itself — token is in HttpOnly cookie).
 * @param token - Refresh token string (existence is tracked, value is not stored)
 */
export const setRefreshToken = (token: string) => {
  if (token) {
    // Store a non-sensitive indicator only (actual token is in HttpOnly cookie)
    return localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, '1');
  }
  return null;
};

/**
 * Retrieves refresh token from localStorage.
 * @returns Refresh token or null
 */
export const getRefreshToken = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * Removes refresh token from localStorage.
 */
export const clearRefreshToken = () => {
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * Stores token expiry timestamp in localStorage.
 * @param expiresIn - Expiry duration in seconds from now
 */
export const setTokenExpiry = (expiresIn: number) => {
  // expiresIn is in seconds, calculate expiry timestamp
  const expiryTime = Date.now() + expiresIn * 1000;
  localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
};

/**
 * Retrieves token expiry timestamp from localStorage.
 * @returns Expiry timestamp in ms or null
 */
export const getTokenExpiry = (): number | null => {
  if (typeof window === 'undefined') return null;
  const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  return expiry ? parseInt(expiry, 10) : null;
};

/**
 * Matches app ProtectedRoute rules: access token plus refresh token or token expiry.
 * Prevents /login ↔ / loops when AUTH_TOKEN exists but session metadata was cleared.
 */
export const hasValidProtectedRouteSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (!getAuthToken()) return false;
  if (!getRefreshToken() && !getTokenExpiry()) return false;
  return true;
};

/**
 * Removes token expiry from localStorage.
 */
export const clearTokenExpiry = () => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
};

/**
 * Checks if token expires within 5 minutes.
 * @returns True if expiring soon or already expired
 */
export const isTokenExpiringSoon = (): boolean => {
  const expiry = getTokenExpiry();
  if (!expiry) return false;

  const now = Date.now();
  const timeUntilExpiry = expiry - now;
  const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

  // Return true if token expires in less than 5 minutes or already expired
  return timeUntilExpiry < fiveMinutes;
};
